import { useCallback, useEffect, useState } from "react";
import { CalendarClock, MapPin, Link2, Plus, Pencil, XCircle, Reply } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Panel, Empty } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, fmtDate } from "@/lib/api";
import { ConvocationDialog, RespondConvDialog } from "@/components/netsoci/ConvocationDialogs";

export const RESP = { accept: "Accettata", decline: "Rifiutata", unavailable: "Non disponibile", propose: "Propone altra data" };
const RESP_CLS = { accept: "bg-primary/10 text-primary", decline: "bg-destructive/10 text-destructive", unavailable: "bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-200", propose: "bg-sky-100 text-sky-800 dark:bg-sky-500/15 dark:text-sky-200" };

function ConvCard({ c, me, onEdit, onRespond, onCancel }) {
  const mine = c.organizer_id === me;
  const myResp = c.responses[me];
  const cancelled = c.status === "cancelled";
  return (
    <Panel className={cancelled ? "opacity-60" : ""} testId={`convocation-${c.id}`}>
      <div className="space-y-3 p-5">
        <div className="flex flex-wrap items-start gap-3">
          <div className="min-w-0 flex-1">
            <p className="text-xs uppercase tracking-wider text-muted-foreground">{mine ? "Organizzata da te" : `Da ${c.organizer?.name}${c.organizer?.company ? ` · ${c.organizer.company}` : ""}`}{cancelled && " · ANNULLATA"}</p>
            <h3 className="mt-1 text-base font-semibold" data-testid={`convocation-title-${c.id}`}>{c.title}</h3>
          </div>
          {mine && !cancelled && (
            <>
              <Button size="sm" variant="outline" onClick={() => onEdit(c)} data-testid={`convocation-edit-${c.id}`}><Pencil className="mr-1.5 h-3.5 w-3.5" />Modifica</Button>
              <Button size="sm" variant="ghost" className="text-destructive" onClick={() => onCancel(c)} data-testid={`convocation-cancel-${c.id}`}><XCircle className="mr-1.5 h-3.5 w-3.5" />Annulla</Button>
            </>
          )}
          {!mine && !cancelled && <Button size="sm" onClick={() => onRespond(c)} data-testid={`convocation-respond-${c.id}`}><Reply className="mr-1.5 h-3.5 w-3.5" />{myResp ? "Cambia risposta" : "Rispondi"}</Button>}
        </div>
        <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm">
          <span className="flex items-center gap-1.5"><CalendarClock className="h-4 w-4 text-primary" />{fmtDate(c.date)} · {c.start_time} · {c.duration_min} min</span>
          {c.location && <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4 text-primary" />{c.location}</span>}
          {c.online_url && <a href={c.online_url} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-primary hover:underline"><Link2 className="h-4 w-4" />Collegamento online</a>}
          {c.rsvp_by && <span className="text-muted-foreground">Conferma entro {fmtDate(c.rsvp_by)}</span>}
        </div>
        {c.description && <p className="whitespace-pre-wrap text-sm text-muted-foreground">{c.description}</p>}
        {c.agenda && <div className="rounded-md bg-muted/50 p-3 text-sm"><p className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Ordine del giorno</p><p className="whitespace-pre-wrap">{c.agenda}</p></div>}
        <ul className="divide-y rounded-md border text-sm" data-testid={`convocation-invitees-${c.id}`}>
          {c.invitees.map((i) => (
            <li key={i.id} className="flex flex-wrap items-center gap-2 px-3 py-2">
              <span className="flex-1 font-medium">{i.name}<span className="ml-1.5 text-xs font-normal text-muted-foreground">{i.company}</span></span>
              {i.response ? <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${RESP_CLS[i.response.status]}`}>{RESP[i.response.status]}{i.response.proposed_date && ` · ${fmtDate(i.response.proposed_date)} ${i.response.proposed_time || ""}`}</span> : <span className="text-xs text-muted-foreground">In attesa</span>}
              {i.response?.message && <span className="basis-full text-xs text-muted-foreground">“{i.response.message}”</span>}
            </li>
          ))}
        </ul>
      </div>
    </Panel>
  );
}

export function Convocations({ contacts, onChange }) {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [edit, setEdit] = useState(null);
  const [respond, setRespond] = useState(null);
  const load = useCallback(() => api.get("/network/convocations").then((r) => setItems(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const done = () => { load(); onChange(); };
  const cancel = async (c) => {
    try { await api.post(`/network/convocations/${c.id}/cancel`); toast.success("Convocazione annullata"); done(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div>
      <div className="mb-4 flex justify-end">
        <Button onClick={() => setEdit({})} disabled={!contacts.length} data-testid="new-convocation-button"><Plus className="mr-1.5 h-4 w-4" />Nuova convocazione</Button>
      </div>
      {!contacts.length && <p className="mb-4 text-sm text-muted-foreground">Per convocare altri amministratori devi prima essere collegato con loro.</p>}
      {items && items.length === 0 ? <Empty icon={CalendarClock} text="Nessuna convocazione." testId="convocations-empty" /> : (
        <div className="space-y-4">{(items || []).map((c) => <ConvCard key={c.id} c={c} me={user.id} onEdit={setEdit} onRespond={setRespond} onCancel={cancel} />)}</div>
      )}
      {edit && <ConvocationDialog conv={edit} contacts={contacts} onClose={() => setEdit(null)} onDone={done} />}
      {respond && <RespondConvDialog conv={respond} me={user.id} onClose={() => setRespond(null)} onDone={done} />}
    </div>
  );
}
